class StickyForm extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._stickyContainer = null;
    this.priceDisplay = null;
    this.variantInput = null;
    this.planInput = null;
    this.freqButtons = null;
    this.selectedPlanId = null;
  }

  connectedCallback() {
    this._stickyContainer = this.querySelector('.sticky-form__container');
    this.priceDisplay = this.querySelector('.sticky-price');
    this.variantInput = this.querySelector('#sticky-variant');

    this.planInput = document.createElement('input');
    this.planInput.type = 'hidden';
    this.planInput.name = 'selling_plan';
    this.appendChild(this.planInput);

    this._bindVariantButtons();
    this._bindFrequencyButtons();
    this._bindFormSubmit();
    this._bindToggleOptions();
    this._updatePrice();
    this._setInitialVisibility();
    this._initObserver();
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  _bindVariantButtons() {
    this.querySelectorAll('.variant-buttons.quantities').forEach(group => {
      group.addEventListener('click', event => {
        const btn = event.target.closest('button.variant-button');
        if (!btn || btn.disabled) return;
        group.querySelectorAll('button.variant-button.active').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.variantInput.value = btn.dataset.variantId;
        this._updatePrice();
      });
    });
  }

  _bindFrequencyButtons() {
    this.freqButtons = this.querySelectorAll('.variant-buttons.frequency button.variant-button');
    this.freqButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.freqButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.selectedPlanId = btn.dataset.sellingPlanId || null;
        this.planInput.value = this.selectedPlanId || '';
        this._updatePrice();
      });
    });
    if (this.freqButtons && this.freqButtons.length) {
      const first = this.freqButtons[0];
      first.classList.add('active');
      this.selectedPlanId = first.dataset.sellingPlanId || null;
      this.planInput.value = this.selectedPlanId || '';
    }
  }

  _bindFormSubmit() {
    const form = this.querySelector('form');
    if (!form) return;
    form.addEventListener('submit', event => {
      event.preventDefault();
      event.stopPropagation();
      this._directCheckout();
    });
  }

  _bindToggleOptions() {
    const toggleBtn = this.querySelector('.toggle-options');
    if (!toggleBtn) return;
    const toggleText = toggleBtn.querySelector('.toggle-text');
    toggleBtn.addEventListener('click', () => {
      const isOpen = this.classList.toggle('open');
      toggleText.textContent = isOpen ? 'Fermer les options' : 'Ouvrir les options';
    });
  }

  _updatePrice() {
    const activeQty = this.querySelector('.variant-buttons.quantities button.variant-button.active');
    if (!activeQty || !this.priceDisplay) return;
    const priceStr = activeQty.dataset.price.replace(/[\s ]/g, '');
    const baseCents = Math.round(parseFloat(priceStr.replace(',', '.').replace(/[^0-9\.]/g, '')) * 100);
    let finalCents = baseCents;

    const freqActive = this.querySelector('.variant-buttons.frequency button.variant-button.active');
    if (freqActive && freqActive.dataset.discount) {
      const discountPerc = parseFloat(freqActive.dataset.discount);
      if (!isNaN(discountPerc)) {
        finalCents = Math.round(baseCents * (100 - discountPerc) / 100);
      }
    }

    this.priceDisplay.textContent = (finalCents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  }

  _directCheckout() {
    const vid = parseInt(this.variantInput?.value, 10);
    if (isNaN(vid)) return;

    const planId = this.selectedPlanId ? parseInt(this.selectedPlanId, 10) : null;

    const item = { id: vid, quantity: 1 };
    const payloadItem = planId ? { ...item, selling_plan: planId } : item;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [payloadItem] })
    })
      .then(res => res.ok ? res.json() : res.json().then(err => Promise.reject(err)))
      .then(data => {
        try {
          sessionStorage.setItem('sidecart:last', JSON.stringify({
            cart: data,
            variantId: vid,
            planId,
            sellingPlan: planId
          }));
        } catch (e) {}

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: data, variantId: vid, planId, sellingPlan: planId }
        }));
      })
      .catch(() => {
        alert('Une erreur est survenue');
      });
  }

  _getTargetSection() {
    return document.querySelector('section[data-section-type="w-main-product"], section[data-section-type="main-product"]');
  }

  _isInViewWithMargins(el, marginTop, marginBottom) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const topLimit = 0 - marginTop;
    const bottomLimit = vh + marginBottom;
    return rect.bottom > topLimit && rect.top < bottomLimit;
  }

  _setInitialVisibility() {
    if (!this._stickyContainer) return;
    const target = this._getTargetSection();
    if (!target) {
      this._stickyContainer.classList.add('is-sticky');
      this._stickyContainer.classList.remove('is-exiting');
      return;
    }
    const inView = this._isInViewWithMargins(target, 100, 100);
    if (inView) {
      this._stickyContainer.classList.remove('is-sticky');
      this._stickyContainer.classList.add('is-exiting');
    } else {
      this._stickyContainer.classList.add('is-sticky');
      this._stickyContainer.classList.remove('is-exiting');
    }
  }

  _initObserver() {
    const target = this._getTargetSection();
    if (!target || !this._stickyContainer) return;
    this._observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this._stickyContainer.classList.remove('is-sticky');
          this._stickyContainer.classList.add('is-exiting');
        } else {
          this._stickyContainer.classList.add('is-sticky');
          this._stickyContainer.classList.remove('is-exiting');
        }
      });
    }, { rootMargin: '-100px 0px -100px 0px', threshold: 0 });
    this._observer.observe(target);
  }
}

customElements.define('sticky-form', StickyForm);
